import { useState, useEffect } from 'react';
import { apiGet } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, 
  Search,
  Plane,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWorkerPhoto } from '@/hooks/useWorkerPhoto';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// Real airplane images
const airplaneImages = [
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1570710891163-6d3b5c47248b?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1559268950-2d7ceb2efa3a?w=400&h=300&fit=crop',
];

// Airport codes
const airportInfo = {
  'DXB': 'DUBAI',
  'AUH': 'ABU DHABI',
  'MNL': 'MANILA',
  'CGK': 'JAKARTA',
  'CMB': 'COLOMBO',
  'DAC': 'DHAKA',
  'DEL': 'NEW DELHI',
  'KTM': 'KATHMANDU',
  'KWI': 'KUWAIT',
  'HKG': 'HONG KONG',
  'DOH': 'DOHA',
  'MCT': 'MUSCAT',
};

// Status config
const statusDisplay = {
  'SCHEDULED': { text: 'ON TIME', color: 'bg-green-500' },
  'ON_TIME': { text: 'ON TIME', color: 'bg-green-500' },
  'DEPARTED': { text: 'DEPARTED', color: 'bg-blue-500' },
  'IN_AIR': { text: 'IN FLIGHT', color: 'bg-cyan-500' },
  'LANDED': { text: 'LANDED', color: 'bg-green-500' },
  'ARRIVED': { text: 'ARRIVED', color: 'bg-green-500' },
  'DELAYED': { text: 'DELAYED', color: 'bg-amber-500', blink: true },
  'CANCELLED': { text: 'CANCELLED', color: 'bg-red-600', blink: true },
};

// Current time
const CurrentTime = () => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-amber-400 font-mono">
        {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
      </div>
      <div className="text-amber-400/60 text-xs">
        {time.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
      </div>
    </div>
  );
};

// Flight Card Component
const FlightCard = ({ flight, index }) => {
  const status = flight.flightStatus?.status || flight.status || 'SCHEDULED';
  const statusConf = statusDisplay[status] || statusDisplay['SCHEDULED'];
  
  const flightNumber = flight.flights?.[0]?.flightNumber || flight.flightNumber || 'N/A';
  const airline = flight.flights?.[0]?.airline || flight.airline || 'Unknown';
  const departureAirport = flight.flights?.[0]?.departureAirport || flight.departureAirport || '---';
  const arrivalAirport = flight.flights?.[0]?.arrivalAirport || flight.arrivalAirport || '---';
  const departureTime = flight.flights?.[0]?.departureTime || flight.departureTime || '--:--';
  
  const depCity = airportInfo[departureAirport] || departureAirport;
  const arrCity = airportInfo[arrivalAirport] || arrivalAirport;
  
  // Passenger info
  const linkedPassenger = flight.linkedPassengers?.[0] || null;
  const passengerName = linkedPassenger?.workerName || flight.passengerNames?.[0] || '';
  const lazyPassengerPhoto = useWorkerPhoto(
    linkedPassenger?.caseId ? { id: linkedPassenger.caseId, has_photo: true } : null
  );
  const passengerPhoto = linkedPassenger?.workerPhoto || lazyPassengerPhoto || null;
  const isLinked = !!linkedPassenger;
  
  const airplaneImg = airplaneImages[index % airplaneImages.length];
  
  return (
    <div className="bg-slate-900/90 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-700/50 mb-4">
      {/* Airplane Image - Full Width */}
      <div className="w-full h-32 relative overflow-hidden">
        <img 
          src={airplaneImg} 
          alt="Aircraft"
          className="w-full h-full object-cover"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        
        {/* Status Badge on Image */}
        <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-lg ${statusConf.color} ${statusConf.blink ? 'animate-pulse' : ''}`}>
          <span className="text-white text-xs font-bold">{statusConf.text}</span>
        </div>
        
        {/* Time & Flight on Image */}
        <div className="absolute bottom-3 left-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-amber-400 font-mono drop-shadow-lg">
              {departureTime}
            </span>
            <div>
              <span className="text-lg font-bold text-cyan-400 font-mono">{flightNumber}</span>
              <p className="text-xs text-white/70">{airline}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Flight Info */}
      <div className="p-4">
        {/* Route */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-center flex-1">
            <div className="text-2xl font-bold text-white">{departureAirport}</div>
            <div className="text-xs text-slate-400">{depCity}</div>
          </div>
          
          <div className="flex items-center px-4">
            <div className="w-6 h-[2px] bg-slate-600"></div>
            <Plane className="h-5 w-5 text-cyan-400 mx-2 rotate-90" />
            <div className="w-6 h-[2px] bg-slate-600"></div>
          </div>
          
          <div className="text-center flex-1">
            <div className="text-2xl font-bold text-white">{arrivalAirport}</div>
            <div className="text-xs text-slate-400">{arrCity}</div>
          </div>
        </div>
        
        {/* Passenger */}
        {passengerName && (
          <div className="flex items-center gap-3 pt-3 border-t border-slate-700/50">
            {passengerPhoto ? (
              <img 
                src={formatBase64Image(passengerPhoto)} 
                alt={passengerName}
                className={`w-10 h-10 rounded-lg object-cover border-2 ${isLinked ? 'border-green-500' : 'border-slate-600'}`}
              />
            ) : (
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLinked ? 'bg-green-500/20 border-2 border-green-500' : 'bg-slate-700'}`}>
                <Plane className="h-5 w-5 text-slate-400 rotate-45" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${isLinked ? 'text-green-400' : 'text-white'}`}>
                {passengerName}
              </p>
              {isLinked && (
                <p className="text-xs text-green-500">✓ LINKED WORKER</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ArrivalsMapPage = () => {
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayMode, setDisplayMode] = useState('departures');

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await apiGet('/api/tickets');
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load flight data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    const interval = setInterval(loadTickets, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter & Sort
  const filteredTickets = tickets.filter(t => {
    const passengerName = t.passengerNames?.[0] || t.passengerName || '';
    const flightNumber = t.flightNumber || '';
    const search = searchTerm.toLowerCase();
    return passengerName.toLowerCase().includes(search) ||
           flightNumber.toLowerCase().includes(search);
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const timeA = a.departureTime || '99:99';
    const timeB = b.departureTime || '99:99';
    return timeA.localeCompare(timeB);
  });

  // Stats
  const stats = {
    total: tickets.length,
    onTime: tickets.filter(t => ['SCHEDULED', 'ON_TIME'].includes(t.status || 'SCHEDULED')).length,
    delayed: tickets.filter(t => t.status === 'DELAYED').length,
    linked: tickets.filter(t => t.linkedPassengers?.length > 0).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-950 to-black" data-testid="arrivals-map-page">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-sm border-b border-cyan-500/30 sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Back + Title */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="h-10 w-10 rounded-xl bg-slate-800 hover:bg-slate-700 text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  <Plane className="h-5 w-5 text-cyan-400" />
                  FLIGHT INFO
                </h1>
                <p className="text-cyan-400/60 text-[10px]">معلومات الرحلات</p>
              </div>
            </div>
            
            {/* Time */}
            <CurrentTime />
            
            {/* Refresh */}
            <Button 
              onClick={loadTickets}
              size="icon"
              className="h-10 w-10 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-slate-900/90 backdrop-blur-sm border-b border-slate-700/50 px-4 py-3">
        {/* Mode Toggle */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setDisplayMode('departures')}
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${
              displayMode === 'departures'
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-800 text-slate-400'
            }`}
          >
            ✈️ DEPARTURES
          </button>
          <button
            onClick={() => setDisplayMode('arrivals')}
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${
              displayMode === 'arrivals'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800 text-slate-400'
            }`}
          >
            🛬 ARRIVALS
          </button>
        </div>
        
        {/* Stats */}
        <div className="flex items-center justify-between mb-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
            <span className="text-white">{stats.total}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-400"></div>
            <span className="text-white">{stats.onTime} On Time</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            <span className="text-white">{stats.delayed} Delayed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <span className="text-white">{stats.linked} Linked</span>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search flight or passenger..."
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-xl"
          />
        </div>
      </div>

      {/* Flight List */}
      <div className="px-4 py-4 pb-20">
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-52 rounded-xl bg-slate-800/50" />
            ))}
          </div>
        ) : sortedTickets.length === 0 ? (
          <div className="py-16 text-center bg-slate-900/50 rounded-2xl">
            <Plane className="h-16 w-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-500 mb-2">NO FLIGHTS</h3>
            <p className="text-slate-600 mb-4 text-sm">
              {searchTerm ? 'No flights found' : 'Upload tickets to see them here'}
            </p>
            <Button 
              onClick={() => navigate('/flight-scanner')}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Upload Tickets
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div>
            {sortedTickets.map((ticket, index) => (
              <FlightCard key={ticket.id || index} flight={ticket} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 py-2 border-t border-slate-700/50">
        <div className="flex items-center justify-center gap-4 text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-400"></div>
            ON TIME
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            DELAYED
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
            DEPARTED
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            CANCELLED
          </span>
        </div>
      </div>
    </div>
  );
};

export default ArrivalsMapPage;
